import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContentType } from '@vivport/backend/types';
import { executeTool } from '@/api/tools-api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { ItemSelector } from '@/components/widget/item-selector';
import { useContentList } from '@/hooks/useContentQueries';
import { toContentHref } from '@/utils/content-ref';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';

type CreatableType = 'ANCHOR' | 'EVENT' | 'URL';

interface CreateAnchorOutput {
  id: number;
  code?: string;
}

interface CreateEventOutput {
  id: number;
  code?: string;
}

interface CiteUrlOutput {
  id?: number;
  attachedToSectionId?: number;
  error?: string;
}

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'pending_approval' }
  | { kind: 'failed'; error: string };

interface CreateContentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialType?: CreatableType;
}

export function CreateContentDialog({ open, onOpenChange, initialType }: CreateContentDialogProps) {
  const navigate = useNavigate();
  const [type, setType] = useState<CreatableType>(initialType ?? 'ANCHOR');
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [parentAnchorId, setParentAnchorId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [url, setUrl] = useState('');
  const [sectionContentId, setSectionContentId] = useState<number | null>(null);

  const anchorsQuery = useContentList({ contentTypes: [ContentType.ANCHOR], limit: 100 });
  const documentsQuery = useContentList({ contentTypes: [ContentType.DOCUMENT], limit: 100 });

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setParentAnchorId(null);
    setStartDate('');
    setEndDate('');
    setUrl('');
    setSectionContentId(null);
    setSubmitState({ kind: 'idle' });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      resetForm();
    }
    onOpenChange(next);
  };

  const navigateToCreated = (id: number, code: string | undefined) => {
    handleOpenChange(false);
    navigate(toContentHref(code && code.length > 0 ? code : String(id)));
  };

  const clientValidationError = (): string | null => {
    if (type === 'EVENT') {
      if (parentAnchorId === null) return 'Choose the Anchor this Event belongs to.';
      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        return 'End must be after start.';
      }
    }
    if (type === 'URL') {
      if (url.trim().length === 0) return 'Enter the URL.';
      if (title.trim().length === 0) return 'Enter a title for the citation.';
      if (description.trim().length === 0) return 'Enter a description for the citation.';
      if (sectionContentId === null) return 'Choose the document section to cite this URL into.';
    }
    return null;
  };

  const handleSubmit = async () => {
    if (submitState.kind === 'submitting') return;
    const clientError = clientValidationError();
    if (clientError) {
      setSubmitState({ kind: 'failed', error: clientError });
      return;
    }
    setSubmitState({ kind: 'submitting' });
    try {
      if (type === 'ANCHOR') {
        const response = await executeTool<
          { title?: string; description?: string },
          CreateAnchorOutput
        >('content.createAnchor', {
          ...(title.trim().length > 0 ? { title: title.trim() } : {}),
          ...(description.trim().length > 0 ? { description: description.trim() } : {}),
        });
        if (response.status === 'PENDING_APPROVAL') {
          setSubmitState({ kind: 'pending_approval' });
          return;
        }
        if (response.status === 'FAILED' || !response.output) {
          setSubmitState({ kind: 'failed', error: response.error ?? 'Creation failed.' });
          return;
        }
        navigateToCreated(response.output.id, response.output.code);
        return;
      }
      if (type === 'EVENT') {
        const response = await executeTool<
          {
            parentAnchorId: number;
            title?: string;
            description?: string;
            startDate?: string;
            endDate?: string;
          },
          CreateEventOutput
        >('content.createEvent', {
          parentAnchorId: parentAnchorId as number,
          ...(title.trim().length > 0 ? { title: title.trim() } : {}),
          ...(description.trim().length > 0 ? { description: description.trim() } : {}),
          ...(startDate ? { startDate: new Date(startDate).toISOString() } : {}),
          ...(endDate ? { endDate: new Date(endDate).toISOString() } : {}),
        });
        if (response.status === 'PENDING_APPROVAL') {
          setSubmitState({ kind: 'pending_approval' });
          return;
        }
        if (response.status === 'FAILED' || !response.output) {
          setSubmitState({ kind: 'failed', error: response.error ?? 'Creation failed.' });
          return;
        }
        navigateToCreated(response.output.id, response.output.code);
        return;
      }
      const response = await executeTool<
        { url: string; title: string; description: string; sectionContentId: number },
        CiteUrlOutput
      >('content.citeUrl', {
        url: url.trim(),
        title: title.trim(),
        description: description.trim(),
        sectionContentId: sectionContentId as number,
      });
      if (response.status === 'PENDING_APPROVAL') {
        setSubmitState({ kind: 'pending_approval' });
        return;
      }
      if (response.status === 'FAILED' || !response.output || response.output.error) {
        setSubmitState({
          kind: 'failed',
          error: response.output?.error ?? response.error ?? 'Creation failed.',
        });
        return;
      }
      if (typeof response.output.id === 'number') {
        navigateToCreated(response.output.id, undefined);
        return;
      }
      if (typeof response.output.attachedToSectionId === 'number') {
        navigateToCreated(response.output.attachedToSectionId, undefined);
        return;
      }
      handleOpenChange(false);
    } catch (error) {
      setSubmitState({ kind: 'failed', error: getAxiosErrorDisplayMessage(error, 'Creation failed.') });
    }
  };

  const toSelectorItem = (item: { id: number; title?: string | null }) => ({
    id: item.id,
    title: typeof item.title === 'string' ? item.title : null,
  });
  const anchorItems = (anchorsQuery.data?.items ?? []).map(toSelectorItem);
  const documentItems = (documentsQuery.data?.items ?? []).map(toSelectorItem);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create content</DialogTitle>
          <DialogDescription>
            {type === 'URL'
              ? 'Save a URL citation into a document section — the same action Neo performs when citing a source.'
              : 'Created through the same governed tools Neo uses.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Tabs
            value={type}
            onValueChange={(value) => {
              setType(value as CreatableType);
              setSubmitState({ kind: 'idle' });
            }}
          >
            <TabsList>
              <TabsTrigger value="ANCHOR">Anchor</TabsTrigger>
              <TabsTrigger value="EVENT">Event</TabsTrigger>
              <TabsTrigger value="URL">URL</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="mt-4 flex flex-col gap-3">
            {type === 'EVENT' && (
              <div className="flex flex-col gap-1.5">
                <Label>Anchor</Label>
                <ItemSelector
                  entityName="anchor"
                  displayName="Anchor"
                  items={anchorItems}
                  selectedId={parentAnchorId}
                  onSelect={setParentAnchorId}
                  loading={anchorsQuery.isLoading}
                />
                {anchorItems.length === 0 && !anchorsQuery.isLoading && (
                  <p className="text-sm text-muted-foreground">
                    No anchors yet — create an Anchor first.
                  </p>
                )}
              </div>
            )}
            {type === 'URL' && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="create-content-url">URL</Label>
                <Input
                  id="create-content-url"
                  type="url"
                  placeholder="https://…"
                  value={url}
                  onChange={(event) => setUrl(event.target.value)}
                />
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="create-content-title">
                Title{type === 'URL' ? '' : ' (optional)'}
              </Label>
              <Input
                id="create-content-title"
                value={title}
                maxLength={type === 'URL' ? 200 : undefined}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="create-content-description">
                Description{type === 'URL' ? '' : ' (optional)'}
              </Label>
              <Textarea
                id="create-content-description"
                value={description}
                maxLength={type === 'URL' ? 2000 : undefined}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
            {type === 'EVENT' && (
              <div className="flex gap-3">
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor="create-content-start">Start (optional)</Label>
                  <Input
                    id="create-content-start"
                    type="datetime-local"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                  />
                </div>
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor="create-content-end">End (optional)</Label>
                  <Input
                    id="create-content-end"
                    type="datetime-local"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                  />
                </div>
              </div>
            )}
            {type === 'URL' && (
              <div className="flex flex-col gap-1.5">
                <Label>Cite into document section</Label>
                <ItemSelector
                  entityName="document section"
                  displayName="Document"
                  items={documentItems}
                  selectedId={sectionContentId}
                  onSelect={setSectionContentId}
                  loading={documentsQuery.isLoading}
                />
              </div>
            )}
            {submitState.kind === 'failed' && (
              <p className="text-sm text-destructive">{submitState.error}</p>
            )}
            {submitState.kind === 'pending_approval' && (
              <p className="text-sm text-muted-foreground">
                Awaiting approval in Mission Control. The item will be created once an admin
                approves it.
              </p>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleSubmit();
            }}
            disabled={submitState.kind === 'submitting' || submitState.kind === 'pending_approval'}
          >
            {submitState.kind === 'submitting' ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
